// Available events that can trigger playbooks
export const EVENTS = [
  {
    id: 'transaction.completed',
    name: 'Transaction Completed',
    description: 'Triggered when a payment transaction is successfully completed',
    category: 'payments',
    payload: {
      transaction_id: 'string',
      booking_reference: 'string',
      payment_link_id: 'string',
      payment_link_amount: 'number',
      transaction_amount: 'number',
      currency: 'string',
      payment_method: 'card | open_banking | direct_debit',
      payment_time: 'datetime',
      customer: {
        email: 'string',
        name: 'string'
      }
    }
  },
  {
    id: 'transaction.failed',
    name: 'Transaction Failed',
    description: 'Triggered when a payment transaction fails',
    category: 'payments',
    payload: {
      transaction_id: 'string',
      booking_reference: 'string',
      error_code: 'string',
      error_message: 'string'
    }
  },
  {
    id: 'refund.requested',
    name: 'Refund Requested',
    description: 'Triggered when a refund is requested',
    category: 'refunds',
    payload: {
      refund_id: 'string',
      transaction_id: 'string',
      amount: 'number',
      reason: 'string'
    }
  },
  {
    id: 'refund.completed',
    name: 'Refund Completed',
    description: 'Triggered when a refund is successfully processed',
    category: 'refunds',
    payload: {
      refund_id: 'string',
      transaction_id: 'string',
      amount: 'number'
    }
  },
  {
    id: 'chargeback.created',
    name: 'Chargeback Created',
    description: 'Triggered when a chargeback is initiated',
    category: 'disputes',
    payload: {
      chargeback_id: 'string',
      transaction_id: 'string',
      amount: 'number',
      reason_code: 'string'
    }
  },
  {
    id: 'payment_link.created',
    name: 'Payment Link Created',
    description: 'Triggered when a new payment link is created',
    category: 'payment_links',
    payload: {
      payment_link_id: 'string',
      amount: 'number',
      currency: 'string',
      reference: 'string'
    }
  },
  {
    id: 'booking.created',
    name: 'Booking Created',
    description: 'Triggered when a new booking is created',
    category: 'bookings',
    payload: {
      booking_id: 'string',
      reference: 'string',
      customer: {
        email: 'string',
        name: 'string'
      }
    }
  }
];

// Available integrations for playbook actions
export const INTEGRATIONS = [
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    icon: 'pipedrive',
    actions: ['search_deal', 'update_deal', 'create_deal', 'get_deal_fields'],
    description: 'CRM integration for deal management'
  },
  {
    id: 'xero',
    name: 'Xero',
    icon: 'xero',
    actions: ['search_contact', 'create_contact', 'search_invoice', 'create_invoice', 'create_payment'],
    description: 'Accounting integration for invoicing and payments'
  },
  {
    id: 'email',
    name: 'Email',
    icon: 'email',
    actions: ['send_email', 'send_template'],
    description: 'Send email notifications'
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: 'slack',
    actions: ['send_message', 'send_alert'],
    description: 'Send Slack notifications'
  },
  {
    id: 'felloh',
    name: 'Felloh',
    icon: 'felloh',
    actions: ['get_transaction', 'get_booking', 'update_booking'],
    description: 'Internal Felloh operations'
  }
];

// Dummy data for playbooks in n8n workflow format
export const PLAYBOOKS = [
  {
    id: 'f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0c',
    meta: {
      enabled: true,
      runs: 234,
      lastRun: '2025-01-15T16:22:00Z',
      tags: ['pipedrive', 'xero', 'invoicing', 'payments', 'integration'],
      integrations: ['pipedrive', 'xero', 'email']
    },
    name: 'Pipedrive & Xero Payment Sync',
    active: true,
    versionId: 'v1.0',
    settings: { executionOrder: 'v1' },
    nodes: [
      {
        id: 'start',
        name: 'New Transaction',
        type: 'n8n-nodes-felloh.trigger',
        typeVersion: 1,
        position: [250, 300],
        parameters: { event: 'transaction.completed' },
        credentials: { fellohApi: { id: '1', name: 'Felloh API' } }
      },
      {
        id: 'find-deal',
        name: 'Find Deal',
        type: 'n8n-nodes-base.pipedrive',
        typeVersion: 1,
        position: [450, 300],
        parameters: {
          operation: 'search',
          resource: 'deal',
          term: '={{ $json.booking_reference }}',
          searchField: 'custom_fields'
        },
        credentials: { pipedriveApi: { id: '3', name: 'Pipedrive' } }
      },
      {
        id: 'check-deal',
        name: 'Deal Found?',
        type: 'n8n-nodes-base.if',
        typeVersion: 1,
        position: [650, 300],
        parameters: {
          conditions: { boolean: [{ value1: '={{ $json.data.length > 0 }}', value2: true }] }
        }
      },
      {
        id: 'find-invoice',
        name: 'Find Invoice',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [850, 200],
        parameters: { operation: 'get', resource: 'invoice', invoiceId: '={{ $json.invoice_number }}' },
        credentials: { xeroOAuth2Api: { id: '4', name: 'Xero' } }
      },
      {
        id: 'check-invoice',
        name: 'Invoice Found?',
        type: 'n8n-nodes-base.if',
        typeVersion: 1,
        position: [1050, 200],
        parameters: { conditions: { boolean: [{ value1: '={{ $json.Invoices.length > 0 }}', value2: true }] } }
      },
      {
        id: 'split-payment',
        name: 'Split by Payment Method',
        type: 'n8n-nodes-base.switch',
        typeVersion: 2,
        position: [1250, 150],
        parameters: {
          dataType: 'string',
          value1: '={{ $json.payment_method }}',
          rules: {
            rules: [
              { value2: 'card', output: 0 },
              { value2: 'open_banking', output: 1 }
            ]
          }
        }
      },
      {
        id: 'card-payment',
        name: 'Create Payment (Card)',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [1450, 100],
        parameters: {
          operation: 'create',
          resource: 'payment',
          invoiceId: '={{ $json.invoice_id }}',
          account: 'Trust Payments CC',
          amount: '={{ $json.payment_link_amount }}'
        }
      },
      {
        id: 'ob-payment',
        name: 'Create Payment (OB)',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [1450, 200],
        parameters: {
          operation: 'create',
          resource: 'payment',
          invoiceId: '={{ $json.invoice_id }}',
          account: 'Natwest Current',
          amount: '={{ $json.payment_link_amount }}'
        }
      },
      {
        id: 'create-invoice',
        name: 'Create Sales Invoice',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [1250, 300],
        parameters: {
          operation: 'create',
          resource: 'invoice',
          type: 'ACCREC',
          contact: '={{ $json.contact_id }}',
          status: 'DRAFT'
        }
      },
      {
        id: 'notify-error',
        name: 'Send Alert',
        type: 'n8n-nodes-base.emailSend',
        typeVersion: 2,
        position: [850, 450],
        parameters: {
          toEmail: 'accounts@company.com',
          subject: 'Felloh Payment - No Deal Found',
          text: 'Payment received for {{ $json.booking_reference }} but no matching deal found.'
        }
      }
    ],
    connections: {
      'start': { main: [[{ node: 'find-deal', type: 'main', index: 0 }]] },
      'find-deal': { main: [[{ node: 'check-deal', type: 'main', index: 0 }]] },
      'check-deal': { main: [[{ node: 'find-invoice', type: 'main', index: 0 }], [{ node: 'notify-error', type: 'main', index: 0 }]] },
      'find-invoice': { main: [[{ node: 'check-invoice', type: 'main', index: 0 }]] },
      'check-invoice': { main: [[{ node: 'split-payment', type: 'main', index: 0 }], [{ node: 'create-invoice', type: 'main', index: 0 }]] },
      'split-payment': { main: [[{ node: 'card-payment', type: 'main', index: 0 }], [{ node: 'ob-payment', type: 'main', index: 0 }]] }
    },
    // Tree structure for visual branching (derived from n8n structure)
    workflowTree: {
      id: 'start',
      name: 'New Transaction',
      type: 'n8n-nodes-felloh.trigger',
      integration: 'felloh',
      description: 'Triggered when a transaction is completed',
      children: [
        {
          id: 'find-deal',
          name: 'Find Deal',
          type: 'n8n-nodes-base.pipedrive',
          integration: 'pipedrive',
          description: 'Search for deal by payment reference',
          children: [
            {
              id: 'check-deal',
              name: 'Deal Found?',
              type: 'n8n-nodes-base.if',
              description: 'Check if matching deal exists',
              branches: [
                {
                  id: 'deal-success',
                  label: 'Success',
                  type: 'success',
                  children: [
                    {
                      id: 'find-invoice',
                      name: 'Find Invoice',
                      type: 'n8n-nodes-base.xero',
                      integration: 'xero',
                      description: 'Search for existing invoice',
                      children: [
                        {
                          id: 'check-invoice',
                          name: 'Invoice Found?',
                          type: 'n8n-nodes-base.if',
                          description: 'Check if invoice exists',
                          branches: [
                            {
                              id: 'invoice-success',
                              label: 'Success',
                              type: 'success',
                              children: [
                                {
                                  id: 'split-payment',
                                  name: 'Split by Payment Method',
                                  type: 'n8n-nodes-base.switch',
                                  description: 'Route based on payment type',
                                  branches: [
                                    {
                                      id: 'card-path',
                                      label: 'Card',
                                      type: 'path',
                                      children: [
                                        {
                                          id: 'card-payment',
                                          name: 'Create Payment',
                                          type: 'n8n-nodes-base.xero',
                                          integration: 'xero',
                                          description: 'Post to Trust Payments CC'
                                        }
                                      ]
                                    },
                                    {
                                      id: 'ob-path',
                                      label: 'Open Banking',
                                      type: 'path',
                                      children: [
                                        {
                                          id: 'ob-payment',
                                          name: 'Create Payment',
                                          type: 'n8n-nodes-base.xero',
                                          integration: 'xero',
                                          description: 'Post to Natwest Current'
                                        }
                                      ]
                                    }
                                  ]
                                }
                              ]
                            },
                            {
                              id: 'invoice-error',
                              label: 'Not Found',
                              type: 'error',
                              children: [
                                {
                                  id: 'create-invoice',
                                  name: 'Create Sales Invoice',
                                  type: 'n8n-nodes-base.xero',
                                  integration: 'xero',
                                  description: 'Create new draft invoice'
                                }
                              ]
                            }
                          ]
                        }
                      ]
                    }
                  ]
                },
                {
                  id: 'deal-error',
                  label: 'Error',
                  type: 'error',
                  children: [
                    {
                      id: 'notify-error',
                      name: 'Send Alert',
                      type: 'n8n-nodes-base.emailSend',
                      integration: 'email',
                      description: 'Notify team of missing deal'
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    // Version history
    versions: [
      {
        id: 'v1.0',
        version: '1.0',
        createdAt: '2024-12-01T10:00:00Z',
        createdBy: 'Adam Smith',
        changeDescription: 'Initial version - Basic Pipedrive to Xero payment sync',
        isCurrent: false
      },
      {
        id: 'v1.1',
        version: '1.1',
        createdAt: '2024-12-15T14:30:00Z',
        createdBy: 'Adam Smith',
        changeDescription: 'Added payment method splitting for Card vs Open Banking',
        isCurrent: false
      },
      {
        id: 'v1.2',
        version: '1.2',
        createdAt: '2025-01-05T09:15:00Z',
        createdBy: 'Sarah Johnson',
        changeDescription: 'Added error notification when deal not found in Pipedrive',
        isCurrent: false
      },
      {
        id: 'v1.3',
        version: '1.3',
        createdAt: '2025-01-10T16:45:00Z',
        createdBy: 'Adam Smith',
        changeDescription: 'Added invoice creation step when invoice not found in Xero',
        isCurrent: true
      }
    ],
    // Execution logs
    logs: [
      {
        id: 'log-001',
        executionId: 'exec-a1b2c3d4',
        startedAt: '2025-01-15T16:22:00Z',
        completedAt: '2025-01-15T16:22:03Z',
        duration: 3200,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0892', payment_method: 'card', amount: 1250.00 },
        stepsExecuted: 7,
        version: '1.3'
      },
      {
        id: 'log-002',
        executionId: 'exec-b2c3d4e5',
        startedAt: '2025-01-15T15:10:00Z',
        completedAt: '2025-01-15T15:10:02Z',
        duration: 2100,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0891', payment_method: 'open_banking', amount: 890.50 },
        stepsExecuted: 7,
        version: '1.3'
      },
      {
        id: 'log-003',
        executionId: 'exec-c3d4e5f6',
        startedAt: '2025-01-15T14:05:00Z',
        completedAt: '2025-01-15T14:05:04Z',
        duration: 4500,
        status: 'error',
        triggerData: { booking_reference: 'FEL-2025-0890', payment_method: 'card', amount: 2100.00 },
        stepsExecuted: 3,
        error: { step: 'Find Deal', message: 'Pipedrive API rate limit exceeded' },
        version: '1.3'
      },
      {
        id: 'log-004',
        executionId: 'exec-d4e5f6a7',
        startedAt: '2025-01-15T12:30:00Z',
        completedAt: '2025-01-15T12:30:05Z',
        duration: 5200,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0889', payment_method: 'card', amount: 450.00 },
        stepsExecuted: 8,
        version: '1.3'
      },
      {
        id: 'log-005',
        executionId: 'exec-e5f6a7b8',
        startedAt: '2025-01-15T11:15:00Z',
        completedAt: '2025-01-15T11:15:02Z',
        duration: 1800,
        status: 'warning',
        triggerData: { booking_reference: 'FEL-2025-0888', payment_method: 'open_banking', amount: 3200.00 },
        stepsExecuted: 5,
        warning: { step: 'Find Invoice', message: 'Invoice not found, created new invoice' },
        version: '1.3'
      },
      {
        id: 'log-006',
        executionId: 'exec-f6a7b8c9',
        startedAt: '2025-01-14T16:45:00Z',
        completedAt: '2025-01-14T16:45:03Z',
        duration: 2900,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0887', payment_method: 'card', amount: 780.00 },
        stepsExecuted: 7,
        version: '1.3'
      },
      {
        id: 'log-007',
        executionId: 'exec-a7b8c9d0',
        startedAt: '2025-01-14T14:20:00Z',
        completedAt: '2025-01-14T14:20:06Z',
        duration: 6100,
        status: 'error',
        triggerData: { booking_reference: 'FEL-2025-0886', payment_method: 'open_banking', amount: 1500.00 },
        stepsExecuted: 2,
        error: { step: 'New Transaction', message: 'Invalid payment reference format' },
        version: '1.3'
      },
      {
        id: 'log-008',
        executionId: 'exec-b8c9d0e1',
        startedAt: '2025-01-14T10:00:00Z',
        completedAt: '2025-01-14T10:00:02Z',
        duration: 2400,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0885', payment_method: 'card', amount: 560.00 },
        stepsExecuted: 7,
        version: '1.2'
      }
    ]
  }
];

export const getPlaybookById = (id) => {
  return PLAYBOOKS.find(p => p.id === id);
};

// Helper to get description from first trigger node or generate from name
export const getPlaybookDescription = (playbook) => {
  if (!playbook.nodes || playbook.nodes.length === 0) return '';

  const triggerNode = playbook.nodes.find(n =>
    n.type.includes('webhook') ||
    n.type.includes('trigger') ||
    n.type.includes('cron') ||
    n.type.includes('schedule')
  );

  if (triggerNode?.parameters?.path) {
    return `Triggered by webhook: ${triggerNode.parameters.path}`;
  }
  if (triggerNode?.parameters?.rule?.interval) {
    return `Scheduled workflow running on a cron schedule`;
  }
  if (triggerNode?.parameters?.event) {
    return `Triggered by ${triggerNode.parameters.event} event`;
  }

  return `Automated workflow with ${playbook.nodes.length} steps`;
};

// Helper to derive schedule from n8n nodes
export const getPlaybookSchedule = (playbook) => {
  if (!playbook.nodes) return 'on_event';

  const triggerNode = playbook.nodes.find(n =>
    n.type.includes('cron') ||
    n.type.includes('schedule') ||
    n.type.includes('webhook') ||
    n.type.includes('trigger')
  );

  if (!triggerNode) return 'on_event';

  // Check for cron expression
  if (triggerNode.parameters?.rule?.interval?.[0]?.expression) {
    return triggerNode.parameters.rule.interval[0].expression;
  }

  // Check for interval mode (every X minutes)
  if (triggerNode.parameters?.triggerTimes?.item?.[0]?.mode === 'everyMinute') {
    const mins = triggerNode.parameters.triggerTimes.item[0].minute || 1;
    return `*/${mins} * * * *`;
  }

  // Webhook or event trigger
  if (triggerNode.type.includes('webhook') || triggerNode.type.includes('trigger')) {
    return 'on_event';
  }

  return 'on_event';
};

// Convert n8n nodes to legacy stepsDetail format for StepsView
export const getStepsDetail = (playbook) => {
  if (!playbook.nodes) return [];

  return playbook.nodes.map((node, index) => {
    // Determine step type from n8n node type
    let stepType = 'action';
    if (node.type.includes('webhook') || node.type.includes('trigger') || node.type.includes('cron') || node.type.includes('schedule')) {
      stepType = 'webhook';
    } else if (node.type.includes('if') || node.type.includes('switch')) {
      stepType = 'condition';
    } else if (node.type.includes('wait')) {
      stepType = 'delay';
    } else if (node.type.includes('loop') || node.type.includes('splitInBatches')) {
      stepType = 'loop';
    } else if (node.type.includes('code')) {
      stepType = 'transform';
    }

    // Determine integration from node type
    let integration = null;
    if (node.type.includes('felloh')) {
      integration = 'felloh';
    } else if (node.type.includes('pipedrive')) {
      integration = 'pipedrive';
    } else if (node.type.includes('xero')) {
      integration = 'xero';
    } else if (node.type.includes('slack')) {
      integration = 'slack';
    } else if (node.type.includes('email') || node.type.includes('Email')) {
      integration = 'email';
    }

    return {
      id: node.id,
      name: node.name,
      type: stepType,
      description: node.parameters?.operation
        ? `${node.parameters.operation} operation`
        : `Step ${index + 1} of the workflow`,
      config: {
        ...node.parameters,
        integration
      }
    };
  });
};

export const getStepTypeIcon = (type, integration) => {
  // Integration-specific icons
  if (integration) {
    const integrationIcons = {
      pipedrive: '🔶',
      xero: '💠',
      email: '📧',
      slack: '💬',
      felloh: '💳'
    };
    return integrationIcons[integration] || '🔌';
  }

  const icons = {
    webhook: '🔗',
    condition: '🔀',
    action: '⚡',
    delay: '⏱️',
    schedule: '📅',
    loop: '🔄',
    transform: '🔄'
  };
  return icons[type] || '📦';
};

export const getStepTypeBadgeColor = (type, integration) => {
  // Integration-specific colors
  if (integration) {
    const integrationColors = {
      pipedrive: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
      xero: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
      email: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      slack: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-300',
      felloh: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300'
    };
    return integrationColors[integration] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }

  const colors = {
    webhook: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    condition: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    action: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    delay: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    schedule: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
    loop: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    transform: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
  };
  return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
};

export const getEventById = (eventId) => {
  return EVENTS.find(e => e.id === eventId);
};

export const getIntegrationById = (integrationId) => {
  return INTEGRATIONS.find(i => i.id === integrationId);
};

export const getEventsByCategory = (category) => {
  return EVENTS.filter(e => e.category === category);
};

export const getScheduleLabel = (schedule) => {
  if (!schedule || schedule === 'on_event') {
    return 'On event';
  }

  const parts = schedule.split(' ');
  if (parts.length !== 5) {
    return schedule;
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Every X minutes
  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const mins = minute.replace('*/', '');
    return `Every ${mins} mins`;
  }

  // Every X hours
  if (minute === '0' && hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const hours = hour.replace('*/', '');
    return `Every ${hours} hours`;
  }

  // Daily at specific time
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    return `Daily at ${time}`;
  }

  // Weekly
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days[parseInt(dayOfWeek, 10)] || dayOfWeek;
    return `Weekly on ${day}`;
  }

  // Monthly
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    return `Monthly on day ${dayOfMonth}`;
  }

  // Hourly
  if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every hour';
  }

  return schedule;
};
